'use client';

import * as React from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from "@/lib/utils";
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface CurrencySelectProps {
  currencies: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/**
 * Currency select component with search
 * Allows selecting a single currency from a list with search functionality
 * Consistent with CurrencyMultiSelect UX
 */
export const CurrencySelect = ({
  currencies,
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  id,
}: CurrencySelectProps) => {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? t('currencySelect.placeholder');
  const [open, setOpen] = React.useState(false);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setOpen(false);
  };

  const displayValue = value || resolvedPlaceholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('h-9 w-full justify-between', !value && 'text-muted-foreground', className)}
        >
          <span className="truncate">{displayValue}</span>
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput placeholder={t('currencySelect.searchPlaceholder')} />
          <CommandList>
            <CommandEmpty>{t('currencySelect.noCurrencyFound')}</CommandEmpty>
            <CommandGroup>
              {currencies.map((currency) => {
                const isSelected = value === currency;

                return (
                  <CommandItem key={currency} onSelect={() => handleSelect(currency)}>
                    <div
                      className={cn(
                        'border-primary mr-2 flex size-4 items-center justify-center rounded-sm border',
                        isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="text-primary-foreground size-4" />
                    </div>
                    <span className="text-foreground">{currency}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
